import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";

import { mapToken } from "@/constants/tokens";
import { MAP_ICON_SIZE, icons } from "@/config/icons";
import {
  getMapParams,
  openObjectInfo,
  changeObjectInfo,
  getDatasourceById
} from "@/utils";
import { ModesController } from "@/libs/map-draw/modes/modes-controller";
import { editorStyles } from "@/libs/map-draw/config";
import {
  LayerController,
  TrackController,
  StyleController,
  HeatmapController,
  DiagramController,
  MatricesController,
  EditorController
} from "../controllers";

export const initMap = async component => {
  // create map instance
  const { center, zoom } = getMapParams("mapBI", component.$store);
  const { $store } = component;
  const baselayer = component.baselayers.find(
    l => l.id === component.baselayerId
  );
  const name = baselayer ? baselayer.name : "dark";
  const style =
    name === "dark"
      ? "mapbox://styles/aplussmoscow/ckea67vfv6oxu1aoxuaikbgvm"
      : `mapbox://styles/mapbox/${name}-v9`;

  mapboxgl.accessToken = mapToken;

  // TODO: this is demo access for LTM Project - NEED TO DELETE
  const demoCenter = { lng: 31.806472187271766, lat: 60.00352042542974 };
  const demoZoom = 5.5;

  component.mapgl = new mapboxgl.Map({
    container: "map-container",
    style,
    center: $store.state.demoGuest ? demoCenter : center,
    zoom: $store.state.demoGuest ? demoZoom : zoom,
    preserveDrawingBuffer: true
  });

  // map view handlers
  component.mapgl.on("pitch", e => {
    component.is3d = e.target.getPitch() !== 0;
  });
  component.mapgl.on("rotate", e => {
    component.mapBearing = e.target.getBearing();
  });

  component.mapgl.on("mousemove", e => {
    const { activeLayers } = component.$store.state.map;
    const layers = [...activeLayers, ...activeLayers.map(l => `${l}_icons`)];
    const mapLayers = layers.filter(l => component.mapgl.getLayer(l));
    if (!mapLayers.length) return;

    const { x, y } = e.point;
    const bbox = [[x - 5, y - 5], [x + 5, y + 5]];
    const features = component.mapgl.queryRenderedFeatures(bbox, {
      layers: mapLayers
    });

    if (!features.length) {
      component.mapgl.getCanvas().style.cursor = "";
      component.popupSettings.display = "none";
      component.popupSettings.values = [];
      component.popupListSettings.display = "none";
      component.popupListSettings.values = [];
      return;
    }

    component.mapgl.getCanvas().style.cursor = "pointer";
    const feature = features[0];
    const { properties, layer } = feature;
    const layerId = layer.id.includes("_") ? layer.id.split("_")[0] : layer.id;
    const { fields, enabled } = component.styleConfig[layerId].popup;
    if (fields.length && enabled) {
      component.popupSettings.values = fields.map(key => ({
        key,
        value: properties[key] === "null" ? null : properties[key]
      }));
      component.popupSettings.top = y - 38;
      component.popupSettings.left = x + 10;
      component.popupSettings.display = "block";
    }
  });

  component.mapgl.on("click", e => {
    const { activeLayers } = component.$store.state.map;
    const layers = [...activeLayers, ...activeLayers.map(l => `${l}_icons`)];
    const mapLayers = layers.filter(l => component.mapgl.getLayer(l));
    if (!mapLayers.length) return;

    const { x, y } = e.point;
    const bbox = [[x - 5, y - 5], [x + 5, y + 5]];
    const features = component.mapgl.queryRenderedFeatures(bbox, {
      layers: mapLayers
    });
    if (!features.length) return;
    if (features.length > 1) {
      component.popupListSettings.top = y - 38;
      component.popupListSettings.left = x - 10;
      component.popupListSettings.display = "block";
      component.popupListSettings.values = features;
    } else {
      const feature = features[0];
      const { properties, layer } = feature;
      const layerId = layer.id.includes("_")
        ? layer.id.split("_")[0]
        : layer.id;
      const source_id = component.$store.state.profiles.sourceIdById[layerId];

      if (e.originalEvent.ctrlKey || e.originalEvent.metaKey)
        openObjectInfo(component, source_id, properties.id, "map", feature);
      else
        changeObjectInfo(component, source_id, properties.id, "map", feature);
    }
  });

  // save map params handler
  window.addEventListener("beforeunload", component.saveMapParams);

  // load icons after map loaded (test)
  const loadIcons = () => {
    function importAll(r) {
      const images = {};
      r.keys().map(item => {
        images[item.replace("./", "")] = r(item);
      });
      return images;
    }

    const images = importAll(
      require.context(
        "../../../assets/images/map-icons",
        false,
        /\.(png|jpe?g|svg)$/
      )
    );

    component.$store.commit("SET_ICONS_CONTEXT", images);

    return Promise.all(
      icons.map(
        ({ name }) =>
          new Promise(async resolve => {
            const url = images[name];
            const image = new Image(MAP_ICON_SIZE, MAP_ICON_SIZE);
            image.crossOrigin = "Anonymous";
            image.style.backgroundPosition = "50%, 50%";
            image.style.backgroundSize = "100%";
            image.addEventListener("load", () => {
              component.mapgl.addImage(name, image);
              resolve();
            });
            image.src = url;
          })
      )
    ).then(() => console.log("map icons loaded!"));
  };

  component.mapgl.on("load", async () => {
    await loadIcons();

    const { initialActiveLayers } = component.$store.state.map;

    if (initialActiveLayers && initialActiveLayers.length) {
      for (let i = 0; i < initialActiveLayers.length; i++) {
        const id = initialActiveLayers[i];
        const source_id = component.$store.state.profiles.sourceIdById[id];
        const layer = await getDatasourceById(component, source_id);

        if (layer) {
          const controller =
            layer.datatype === "last_tracks"
              ? "TrackController"
              : "LayerController";
          await component[controller].toggleLayer({ id });
        }
      }
    }
  });
};

export const initControllers = component => {
  component.LayerController = new LayerController(component);
  component.TrackController = new TrackController(component);
  component.StyleController = new StyleController(component);
  component.DiagramController = new DiagramController(component);
  component.HeatmapController = new HeatmapController(component);
  component.MatricesController = new MatricesController(component);

  // editor creation
  const mc = new ModesController(component, MapboxDraw, "editorState");
  component.draw = new MapboxDraw({
    displayControlsDefault: false,
    controls: {
      line_string: true,
      point: true,
      polygon: true,
      trash: true
    },
    styles: editorStyles,
    userProperties: true,
    modes: Object.assign(MapboxDraw.modes, {
      draw_point: mc.createDrawPointMode(),
      draw_line_string: mc.createDrawLineStringMode(),
      draw_polygon: mc.createDrawPolygonMode(),
      draw_nodes: mc.createDrawNodesMode(),
      draw_links: mc.createDrawLinksMode(),
      draw_zones: mc.createDrawZonesMode(),
      draw_connectors: mc.createDrawConnectorsMode(),
      simple_select: mc.createSimpleSelectMode(),
      direct_select: mc.createDirectSelectMode()
    })
  });
  component.EditorController = new EditorController(component);
  component.EditorController.initDrawListeners();
};
